import { useTranslation } from "react-i18next";
import type {
  Assets,
  AssetsSummary,
  PIMProduct,
  PIMProductBase,
  SupportedAssetAccessibility,
  TdsGeneratedAssetSchema,
} from "../../../../../types/types.PIM";
import {
  H3,
  RegularTextSmall,
} from "../../../../../components/Typography/Typography";
import { Flex, Form } from "../../../../../layout/FormLayout";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import { Table } from "../../../../../components/Table/Table";
import {
  PrimaryButtonLargeV2,
  SecondaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { GenerateDocument } from "./GenerateDocument";
import { TextField } from "../../../../../components/TextFields/TextFields";
import {
  TablePlaceholder,
  toTitleCase,
  useFormWrapper,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../../util/util";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import type { AxiosError } from "axios";
import { endpoints } from "../../../../../endpoints";
import { strings } from "../../../../../util/strings";
import type { OptionType, SupportedLanguage } from "../../../../../types/types";
import axios from "axios";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import type { Getter } from "@tanstack/react-table";
import { DocumentAccessibility } from "../../../../../components/DocumentAccessibility/DocumentAccessibility";
import {
  accessibilityRequestData,
  getCustomersExistingData,
  getRolesExistingData,
  getUsersExistingData,
} from "../util";
import useSWR from "swr";

const TableContainer = styled.div`
  align-self: stretch;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  border: solid 1px ${({ theme }) => theme.colors.secondaryLightGray};
`;

const ScrollArea = styled.div`
  max-height: 460px;
  overflow-y: auto;
  display: block;
`;

export const EditOrGenerateProductDocument = forwardRef<
  { clear_poll_timeout: () => void },
  {
    asset?: AssetsSummary;
    product?: PIMProductBase;
    replaceProductId?: (id: string) => void;
    onSuccess: (productId?: string, shouldFetchData?: boolean) => void;
    onCancel: () => void;
  }
>((props, ref) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { notifySuccess, notifyError } = useNotifications();
  const [tableData, setTableData] = useState<
    { name: string; type: "group" | "collection" }[]
  >([]);
  const [asset, setAsset] = useState<Assets>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedAsset, setGeneratedAsset] =
    useState<TdsGeneratedAssetSchema>();
  const [productOptions, setProductOptions] = useState<OptionType<string>[]>();
  const [poll_timeout, set_poll_timeout] = useState<NodeJS.Timeout>();
  const [accessibility, setAccessibility] =
    useState<SupportedAssetAccessibility>(asset?.visibility || "internal");

  useSWR<Assets, AxiosError>(
    props.asset?.is_generated && !asset
      ? `${endpoints.v2_tenants_id_pim_assets_id(
          tenant_id,
          props.asset.id
        )}?is_generated=true`
      : null,
    {
      onSuccess: (data) => {
        if (data.is_generated) {
          onGenerateAsset(data);
          setGeneratedAsset(data.tds_generated_asset);
          if (!tableData.length) {
            setTableData(
              data.tds_generated_asset.sections.map((section) => ({
                name:
                  section.group_type === "group"
                    ? section.group.display_name
                      ? t([section.group.display_name])
                      : section.group.name
                    : section.collection.display_name
                    ? t([section.collection.display_name])
                    : section.collection.name,
                type: section.group_type,
              }))
            );
          }
          if (!productOptions) {
            getProducts(data);
          }
        }
      },
      onError: (error) => {
        notifyError(t("There was an error fetching the generated asset"), {
          error,
        });
      },
    }
  );

  useSWR<PIMProduct, AxiosError>(
    props.product && !props.asset && !generatedAsset
      ? `${endpoints.v2_tenants_id_pim_products_id(
          tenant_id,
          props.product.id
        )}?use_database=true&return_staged=true`
      : null,
    {
      onSuccess: (product) => {
        if (product) {
          const generatedAssetFromProduct =
            product.product_schema.generated_assets.find(
              (genAsset) => genAsset.asset_category === categoryValue.value
            );
          if (
            generatedAssetFromProduct &&
            generatedAssetFromProduct.is_active
          ) {
            setGeneratedAsset(generatedAssetFromProduct);
            if (!tableData.length) {
              setTableData(
                generatedAssetFromProduct.sections.map((section) => ({
                  name:
                    section.group_type === "group"
                      ? section.group.display_name
                        ? t([section.group.display_name])
                        : section.group.name
                      : section.collection.display_name
                      ? t([section.collection.display_name])
                      : section.collection.name,
                  type: section.group_type,
                }))
              );
            }
          }
        }
      },
      onError: (error) => {
        notifyError(t("There was an error fetching the generated asset"), {
          error,
        });
      },
    }
  );

  const updateProductStatus = useUpdateProductStatus({
    product: props.product
      ? props.product
      : ({ status: "archived" } as PIMProduct),
  });

  const defaultValues = useMemo(() => {
    return {
      category: {
        label: "Technical Data Sheet (TDS)",
        value: "tds",
      },
      asset_name: "",
      product_from_asset: { label: "", value: "" },
      product_name: props.product ? props.product.name : "",
      language: { label: "English (EN)", value: "en" },
      input_customer_ids: [],
      input_user_ids: [],
      input_role_ids: [],
    };
  }, [props]);

  const methodsOfUseForm = useFormWrapper({
    defaultValues,
  });

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const categoryValue = watch("category", {
    label: "Technical Data Sheet (TDS)",
    value: "tds",
  });

  const tableColumns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: t("Type"),
        accessorKey: "type",
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <div>{toTitleCase(getValue())}</div>
        ),
      },
    ],
    [t]
  );

  const onGenerateAsset = useCallback(
    (asset: Assets) => {
      setValue("language", { label: "English (EN)", value: "en" });
      setAsset(asset);
      setAccessibility(asset.visibility);
    },
    [setValue]
  );

  const getProducts = useCallback(
    async (asset: Assets) => {
      const products = (
        props.product
          ? [props.product.id]
          : props.asset
          ? asset.product_ids
          : []
      )
        .map((product_id) => `product_id=${product_id}`)
        .join("&");
      const { data: prods } = await axios.get<{ data: Array<PIMProductBase> }>(
        `${endpoints.v2_tenants_id_pim_products_summary(
          tenant_id
        )}?use_database=true&${products}`
      );
      const prodOptions: OptionType<string>[] =
        prods.data.length === 1
          ? [{ label: prods.data[0].name, value: prods.data[0].id }]
          : prods.data
              .filter(({ status }) => status.includes("staged"))
              .map((data) => ({
                label: data.name,
                value: data.id,
              }));
      setProductOptions(prodOptions);
      if (prodOptions.length === 1) {
        setValue("product_from_asset", prodOptions[0]);
      }
    },
    [props.asset, props.product, setValue, tenant_id]
  );

  const update_timeout_id = (timeout_id: NodeJS.Timeout) => {
    set_poll_timeout(timeout_id);
  };

  const changeAssetAccessibility = (
    newAccessibility: string | React.FormEvent<HTMLSelectElement>
  ) => {
    if (typeof newAccessibility === "string") {
      setAccessibility(newAccessibility as SupportedAssetAccessibility);
    } else if (newAccessibility.currentTarget.value) {
      setAccessibility(
        newAccessibility.currentTarget.value as SupportedAssetAccessibility
      );
    }
  };

  const existingAccessibilityData = useMemo(
    () => ({
      users: getUsersExistingData(props.asset?.users),
      roles: getRolesExistingData(
        props.asset?.allow_all_roles,
        props.asset?.roles
      ),
      customers: getCustomersExistingData(
        props.asset?.allow_all_customers,
        props.asset?.allow_all_distributors,
        props.asset?.customers
      ),
    }),
    [props.asset]
  );

  const onSubmit = async ({
    asset_name,
    language,
    input_customer_ids,
    input_user_ids,
    input_role_ids,
  }: {
    asset_name: string;
    language: OptionType<SupportedLanguage>;
    input_customer_ids: string[];
    input_user_ids: string[];
    input_role_ids: string[];
  }) => {
    if (asset) {
      setIsSubmitting(true);
      const requestBody = {
        name: asset_name,
        asset_type: asset.asset_type,
        category_id: asset.category.id,
        language: language.value,
        is_downloadable: asset.is_downloadable ?? false,
        is_cover_image: asset.is_cover_image ?? false,
        id: asset.id,
        unarchive: true,
        visibility: accessibility,
        ...accessibilityRequestData(
          asset.asset_type,
          accessibility,
          input_customer_ids,
          input_user_ids,
          input_role_ids
        ),
      };
      try {
        let updatedProduct: PIMProduct | null = null;
        if (!props.asset) {
          const { data } = await updateProductStatus();
          updatedProduct = data;
        }
        const { data: updatedAsset } = await axios.patch<Assets>(
          endpoints.v2_tenants_id_pim_assets_id(tenant_id, asset.id),
          {
            ...requestBody,
            ...(!props.asset
              ? { product_ids: [updatedProduct!.id] }
              : props.product
              ? { product_ids: [props.product.id] }
              : {}),
          }
        );
        notifySuccess(t("Asset successfully edited"));
        setAsset(updatedAsset);
        setAccessibility(updatedAsset.visibility);
        setIsSubmitting(false);
        props.onSuccess(
          !props.asset
            ? updatedProduct?.primary_staged_product_id ?? updatedProduct?.id
            : "",
          true
        );
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error editing the generated asset"),
          {
            error,
          }
        );
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (asset) {
      const assetNameArr = asset.name.split(".");
      const assetName =
        assetNameArr.length > 1
          ? assetNameArr.slice(0, -1).join("")
          : assetNameArr[0];
      setValue("asset_name", assetName);
    }
  }, [asset, setValue]);

  useImperativeHandle(
    ref,
    () => {
      return {
        clear_poll_timeout() {
          clearTimeout(poll_timeout);
        },
      };
    },
    [poll_timeout]
  );

  const title = asset ? t("Edit Generated Document") : t("Generate Document");
  return (
    <>
      <H3>{title}</H3>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="category"
          placeholder={t("Category")}
          options={[]}
          isDisabled={true}
          errors={errors}
          formState={formState}
        />
        {!props.asset && (
          <TextField
            name="product_name"
            label={t("Product Name")}
            theref={register()}
            formState={formState}
            disabled={true}
            errors={errors}
            type="text"
          />
        )}
        {props.asset && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="product_from_asset"
            placeholder={t("Select Product")}
            options={productOptions ?? []}
            rules={{
              validate: (val) =>
                !props.asset
                  ? true
                  : val?.value
                  ? true
                  : strings(t).thisIsARequiredField,
            }}
            errors={errors}
            isDisabled={productOptions?.length === 1}
            formState={formState}
          />
        )}
        <TableContainer>
          <RegularTextSmall>
            {t("Selected groups & collections")}
          </RegularTextSmall>
          <ScrollArea>
            <Table
              columns={tableColumns}
              data={tableData}
              isLoading={false}
              error={undefined}
              Placeholder={
                <TablePlaceholder message={t("No items to show.")} />
              }
            />
          </ScrollArea>
        </TableContainer>
        {generatedAsset && (
          <GenerateDocument
            asset={asset}
            product_id={
              props.product
                ? props.product.id
                : asset
                ? asset.product_ids[0]
                : ""
            }
            generate_id={generatedAsset.id}
            update_poll_timeout_id={update_timeout_id}
            onGenerate={(asset) => {
              onGenerateAsset(asset);
              notifySuccess(
                props.asset
                  ? t("Generated asset has been updated successfully")
                  : t("Asset has been successfully generated")
              );
            }}
          />
        )}
        <TextField
          name="asset_name"
          label={t("Asset Name")}
          theref={register({ required: strings(t).thisIsARequiredField })}
          formState={formState}
          errors={errors}
          readOnly={!asset}
          type="text"
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="language"
          placeholder={t("Language")}
          options={[{ label: "English (EN)", value: "en" }]}
          errors={errors}
          isDisabled={!asset}
          formState={formState}
        />
        {props.product && (
          <DocumentAccessibility
            accessibility={accessibility}
            methodsOfUseForm={methodsOfUseForm}
            onAccessibilityChange={changeAssetAccessibility}
            existingData={existingAccessibilityData}
          />
        )}
        <Flex style={{ columnGap: "16px" }}>
          <SecondaryButtonMediumV2
            fullWidth
            type="button"
            onClick={props.onCancel}
          >
            {t("Cancel")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonLargeV2
            fullWidth
            loading={isSubmitting}
            type="submit"
            disabled={!asset}
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </Flex>
      </Form>
    </>
  );
});
